import * as React from 'react'
import { mean, omit, sum } from 'lodash-es'
import { CollapsibleTable } from 'src/design-system/components/organisms/CollapsibleTable'
import { useWfiContext } from '../WfiContext'
import { twoDecimals } from '../utils'
import { Skill, Team, User } from '../types'

const HEADINGS = {
  skill: 'Skill',
  avgLevel: 'Avg. Level',
  numLevels: 'No. Levels',
  numVariants: 'No. Variants',
}

type CurrentSkillsProps = {
  selectedTeam: number | null
  teams: Team[]
}

export const CurrentSkills: React.VFC<CurrentSkillsProps> = ({
  selectedTeam,
  teams,
}) => {
  const { orgSnapshot } = useWfiContext()

  if (orgSnapshot == null) return null

  teams =
    selectedTeam == null
      ? teams
      : teams.filter((team) => team.id === selectedTeam).slice(0, 1)
  const users =
    selectedTeam == null
      ? orgSnapshot.users
      : orgSnapshot.users.filter((user) => user.team_id === selectedTeam)

  const groupedRows = [
    {
      name: 'Skills',
      display: 'Skills',
      rows: getSkillRows(teams, users, orgSnapshot.skills).map((skill) =>
        omit(skill, ['id'])
      ),
    },
  ]

  return (
    <div>
      <CollapsibleTable headings={HEADINGS} groupedRows={groupedRows} />
    </div>
  )
}

export const getSkillRows = (teams: Team[], users: User[], skills: Skill[]) => {
  const allPositions = teams
    .map((team) => team.positions)
    .flat()
    .map((position) => ({
      ...position,
      numUsers: users.filter((user) => user.position_id === position.id).length,
    }))
  const allSkills = skills.map((skill) => {
    const positionsWithSkill = allPositions.filter((position) =>
      position.requirements.some((req) => req.skill_id === skill.id)
    )
    const levelsWithNumPositions = positionsWithSkill.map((position) => [
      position.numUsers,
      mean(
        position.requirements
          .filter((req) => req.skill_id === skill.id)
          .map((req) => req.level)
      ),
    ])
    const numPositions = sum(
      levelsWithNumPositions.map(([numUsers]) => numUsers)
    )
    const weightedTotal = sum(
      levelsWithNumPositions.map(([numUsers, level]) => numUsers * level)
    )
    const avgLevel = twoDecimals(
      numPositions === 0 ? 0 : weightedTotal / numPositions
    )
    const skillVariants = new Set(
      positionsWithSkill
        .map((position) => position.requirements)
        .flat()
        .filter((req) => req.skill_id === skill.id)
        .map((req) => req.skill_variant_id)
    )

    return {
      id: skill.id,
      name: skill.name,
      avgLevel,
      numLevels: skill.maxLevel,
      numVariants: skillVariants.size,
    }
  })

  return allSkills
}
